/**
 * Shared plumbing between the halves of API error reporting to Datadog.
 *
 * restapi.js's error_intercept reports every API failure through
 * reportApiErrorToDatadog() with endpoint/method/status context, then re-throws
 * the original axios error so existing callers keep working. RUM then captures
 * that same failure again on its own — once as the unhandled rejection, and once
 * more for every console.error() a caller logs it with — so a single failure can
 * reach Error Tracking three times, twice with no distinguishing context.
 *
 * markApiErrorReported() stamps an axios error that one of our interceptors has
 * already triaged; shouldDiscardRumErrorEvent() is boot/rum.js's beforeSend
 * predicate, which drops RUM's duplicate captures of it and nothing else.
 *
 * Not every axios instance runs error_intercept: the authentication surface
 * (session.js's /user_session client and user.js's api_no_intercept, which
 * carries /login, /logout, /login_check, /verify_email, /verify_otp, …)
 * deliberately opts out of its toasts and redirects. Those instances instead run
 * reportUninterceptedApiError(), which shares this module's triage so their
 * genuine failures still reach Error Tracking exactly once — and their expected
 * 4xx still stay silent.
 */

import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum-slim';

// Non-enumerable so it never reaches the wire via axios's error.toJSON().
const REPORTED = '__chartflowApiErrorReported';

// A SECOND, deliberately distinct stamp. REPORTED means "one of our
// interceptors triaged this and RUM's duplicate capture can be dropped" — and
// EVERY axios instance in the app now sets it, error_intercept for the regular
// clients and reportUninterceptedApiError for the auth surface. So REPORTED
// cannot answer the question a view-level catch actually needs to ask, which is
// not "was this reported?" but "did something already SHOW the user what
// happened and act on it?".
//
// Those two properties come apart precisely on the auth surface: a
// /user_session 401 from session.js's un-intercepted client is stamped REPORTED
// (so RUM stays quiet) while nothing has toasted, redirected or cleared the
// session. A caller that suppressed its own logging on REPORTED alone would go
// dark on exactly the failure the user most needs explained.
//
// HANDLED therefore means one specific thing: the app has ALREADY put the user
// on a path that explains this failure, so a view-level catch that sees it knows
// re-logging would only mint a duplicate Datadog issue via RUM's console
// instrumentation and re-notifying would double the toast (GitLab #895).
//
// Two places qualify, and the bar for adding a third is that same "already
// explained, already navigating" property — never merely "expected":
//   - restapi.js's handle401, which runs the standard expiry sequence
//     (toast + redirect to /login + session clear);
//   - region.js's no-organization guard, which throws only after api() has
//     pushed the visitor to /signup (Datadog 0390a3de).
const HANDLED = '__chartflowApiErrorHandled';

// The two generic axios failure messages. Neither carries the endpoint, method
// or status, and Datadog fingerprints on stack shape rather than message, so
// every distinct failure sharing axios's internal stack collapses into one
// un-actionable issue. Deliberately unanchored: RUM's console channel builds an
// event message by concatenating the console.error() arguments, so the real
// message arrives prefixed with whatever label the caller logged
// ("Error loading courses: AxiosError: Request failed with status code 400").
const GENERIC_AXIOS_FAILURE = /Request failed with status code \d+|\bNetwork Error\b/;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Is this path segment a record id rather than part of the route?
 *
 * Calibrated against every literal segment in the frontend's API calls (194 of
 * them): the route vocabulary is exclusively lowercase and digit-free, and a
 * segment with no `_`/`-` separator caps at 13 characters ("orchestratexr").
 * Our uids are base62 and 27 characters ("2jfchqSBVS2OcxizIIM2Fke33Cg"), so
 * they are caught by both the length and the uppercase/digit test, and the
 * 13-vs-14 margin keeps the longest real route word out.
 *
 * A segment can also be a COMMA-JOINED LIST of ids: `GetLookups` stringifies
 * an array of lookup-type uids straight into the URL
 * (`/lookups/2hdPgl6...,2hdPhad...`), and the backend explicitly supports it
 * (`Lookup.get_all` splits on `,`). The single-segment tests above all fail a
 * comma outright, so every distinct combination of lookup-type uids used to
 * mint its own Error Tracking issue instead of collapsing into one (issue
 * 1087). Splitting first and requiring every piece to be independently
 * opaque keeps this recursive call comma-free, so it terminates, and keeps a
 * segment mixing an id with a literal route word (or a stray empty piece
 * from a leading/trailing/doubled comma) un-rewritten rather than partially
 * masked.
 */
function isOpaqueId(segment) {
  if (segment.includes(',')) {
    const parts = segment.split(',');
    return parts.every((part) => part.length > 0 && isOpaqueId(part));
  }
  if (/^\d+$/.test(segment)) return true;
  if (UUID.test(segment)) return true;
  if (!/^[0-9A-Za-z]+$/.test(segment)) return false;
  return segment.length >= 14 || /[0-9A-Z]/.test(segment);
}

/** `/course/2jfchq.../activity/3HE6tp...` -> `/course/:id/activity/:id` */
export function fingerprintPathname(url) {
  const pathname = String(url ?? '').split(/[?#]/)[0];
  return pathname
    .split('/')
    .map((segment) => (segment && isOpaqueId(segment) ? ':id' : segment))
    .join('/');
}

/**
 * Datadog fingerprints errors on stack shape, and every API failure shares one
 * stack (restapi.js's interceptor), so without an explicit fingerprint they all
 * collapse into a single "API error X0X: *" issue — the exact outcome the
 * enriched reporting set out to prevent, and one that can never be reviewed once
 * and marked safe because it mixes unrelated failures. Grouping on
 * method + route + status instead gives one issue per thing that can actually
 * go wrong. Ids are normalised out so a failing route doesn't mint a fresh issue
 * per course/patient/activity (the #849 URL-explosion problem).
 */
export function apiErrorFingerprint(method, url, status) {
  // Response-less failures collapse to ONE issue per method, deliberately
  // dropping the route. They are not application defects: status 0 means the
  // request never reached the server, so the cause is the device, the network
  // or the tab — nothing in this repo. Grouping them per route minted a new
  // Error Tracking issue for every endpoint that ever dropped a connection (65
  // of them over 30 days: auth, catalog, lookups, charting, course progress),
  // and each new issue pages auto-triage with something no one can fix.
  //
  // Route normalisation alone does not solve it. `fingerprintPathname` only
  // rewrites segments that look opaque, so a lowercase dynamic segment
  // (/organization/{subdomain}/login_options, including typo'd ones) and
  // literal `null`/`undefined` segments still mint their own issue.
  //
  // Method is kept because it is the one distinction that changes what the user
  // is told (see restapi.js's response-less branch) and what it costs them. The
  // specific URL is not lost — it stays on the event as http_url, so the detail
  // is still queryable; it just no longer creates issues.
  if (!status) return `api-error:${method || 'UNKNOWN'} network`;
  return `api-error:${method || 'UNKNOWN'} ${fingerprintPathname(url)} ${status}`;
}

// `*.chartflow.io` is a wildcard CloudFront alternate domain (stacks/site.js),
// so GET /organization/{subdomain}/login_options 404s whenever a bot or typo
// probes a subdomain that isn't a registered organization (see Login.vue /
// WitnessAuthDialog.vue). That's expected behavior for this route, not an
// application error, and every distinct probed subdomain would otherwise
// mint its own Error Tracking issue (fingerprinted on the literal URL below)
// that auto-triage pages on forever. Scoped narrowly to this one endpoint so
// exact-URL grouping still applies everywhere else (see GitLab #849).
const ORG_LOGIN_OPTIONS_404_PATTERN = /^\/organization\/[^/]+\/login_options(?:\?|$)/;

export function isExpectedOrgLoginOptions404(method, url, status) {
  return status === 404 && method === 'GET' && ORG_LOGIN_OPTIONS_404_PATTERN.test(url ?? '');
}

// user.js's loginWithPasskey() (and callers that pass an AbortSignal in
// general) deliberately cancel in-flight requests — e.g. LoginForm.vue's
// onUnmounted aborts the conditional/autofill /auth/login-options request if
// the visitor navigates away before it resolves. That's the ceremony working
// as designed, not a network failure: axios rejects the aborted request with
// no `error.response` (same shape as a real network error), so without this
// check it reads as "API network error: POST /auth/login-options" and mints
// a Datadog Error Tracking issue on ordinary navigation.
export function isCanceledRequest(error) {
  return axios.isCancel(error);
}

// Mirrors the backend's error-context pattern in
// backend/services/api/__init__.py (span.set_tags for user/org/route,
// span.set_exc_info for the actual exception): an API error gets explicitly
// reported with its endpoint/method/status as structured context instead of
// relying on the generic auto-captured unhandled rejection (which is just
// "AxiosError: Request failed with status code NNN" from axios's own internals
// for every single failure in the app, indistinguishable from any other). This
// lets each distinct endpoint+status combination form its own Datadog issue, so
// a specific one can be reviewed once and marked safe/ignored, while
// new/unexpected combinations still surface on their own. boot/rum.js drops the
// resulting duplicate generic event so each failure is reported exactly once.
export function reportApiErrorToDatadog(error) {
  const method = error?.config?.method?.toUpperCase();
  const url = error?.config?.url;
  const status = error?.response?.status;

  if (
    isExpectedOrgLoginOptions404(method, url, status) ||
    isCanceledRequest(error) ||
    // See pageUnloading below: a response-less failure during page teardown is
    // an aborted in-flight request, not a network outage, on EVERY axios
    // instance that reaches this function — not just the un-intercepted ones.
    // error_intercept (restapi.js) calls this directly for default_api, which
    // is what Login.vue's GetOrgLoginOptions call uses; without this check here
    // too, closing the tab or navigating off /login mid-request still filed
    // `api-error:GET /organization/:id/login_options network` once per org
    // subdomain (see ORG_LOGIN_OPTIONS_404_PATTERN above for the 404 sibling of
    // this same explosion).
    (!error?.response && pageUnloading)
  ) {
    return;
  }

  // The network label carries no URL, to match the collapsed fingerprint above:
  // an issue title showing one arbitrary endpoint would misrepresent a group
  // spanning all of them. http_url below keeps the specific request.
  const label = status
    ? `API error ${status}: ${method} ${url}`
    : `API network error: ${method}`;
  const reportedError = new Error(label);
  reportedError.cause = error;
  // Datadog reads dd_fingerprint off the Error instance to override its default
  // stack-shape grouping (browser-core's tryToGetFingerprint). Without it every
  // API failure lands in one issue, because they all share one interceptor
  // stack. See apiErrorFingerprint for the grouping key.
  reportedError.dd_fingerprint = apiErrorFingerprint(method, url, status);
  datadogRum.addError(reportedError, {
    chartflow_reported: true,
    http_method: method,
    http_url: url,
    http_status_code: status,
    response_message: error?.response?.data?.message,
  });
}

// True while the browser is tearing the page down. Closing the tab or
// navigating away aborts every in-flight request, and axios surfaces those
// aborts as a plain response-less `Network Error` — identical in shape to a
// real outage, and NOT caught by isCanceledRequest(), which only recognises an
// explicit AbortSignal raised by our own code.
//
// `pagehide` and `beforeunload` are both registered because neither fires in
// every browser for every navigation.
//
// It must NOT be one-way. `pagehide` also fires when the page is merely
// SUSPENDED into the back/forward cache (`event.persisted === true`), and a
// bfcache restore brings the page back with the JS heap — and this module
// instance — intact. A latch with no reset would leave the flag set for the
// rest of that session, silently disabling outage reporting from the moment the
// visitor pressed Back. `pageshow` fires on every activation, restore or fresh
// load, and means the page is alive and interactive, so clearing on it
// unconditionally is both correct and the safer default: the failure mode of
// clearing too eagerly is a spurious report, the failure mode of not clearing
// is going permanently blind.
let pageUnloading = false;

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => { pageUnloading = true; });
  window.addEventListener('beforeunload', () => { pageUnloading = true; });
  window.addEventListener('pageshow', () => { pageUnloading = false; });
}

// Exposed so any interceptor can apply the same "a response-less rejection
// during teardown is an aborted request, not an outage" rule that
// isUnexpectedUninterceptedFailure applies below — restapi.js's error_intercept
// (the main, non-auth axios instance covering every other API call in the app)
// needs the identical check and has no other way to read this module-private
// flag.
export function isPageUnloading() {
  return pageUnloading;
}

/**
 * Is a failure on an un-intercepted instance worth an Error Tracking issue?
 *
 * These instances are the authentication surface, and every one of their
 * callers treats a 4xx as a normal, message-carrying outcome it renders in the
 * UI: a wrong password on /login, an unverified email on /verify_email, a stale
 * code on /verify_otp, an unrecognised address on /login_check, a
 * /user_session 401 for a visitor whose cookie simply expired (which region.js
 * and restapi.js already handle by rerouting to /login). Reporting those would
 * trade a silence bug for a much worse noise bug, so only failures no caller
 * can act on are reported: a request that never got a response at all — the
 * total-outage-during-login case that previously reported nothing anywhere —
 * and 5xx server faults. A maintenance-mode 503 is planned downtime the callers
 * already handle by redirecting to /maintenance.
 *
 * Explicit cancellations are not filtered here — reportApiErrorToDatadog owns
 * that rule for both interceptors, so duplicating it would just give the same
 * behaviour two owners.
 */
export function isUnexpectedUninterceptedFailure(error) {
  const response = error?.response;
  // See pageUnloading: once the browser is tearing the page down, a
  // response-less rejection is far more likely to be an aborted in-flight
  // request than an outage, and nothing in the error distinguishes them. This
  // surface has to stay trustworthy for "is the backend down during login?",
  // which means not filing issues we can't stand behind.
  if (!response) return !pageUnloading;
  if (response.status === 503 && response.data?.maintenance_mode) return false;
  return response.status >= 500;
}

/**
 * Response-error interceptor for the axios instances that deliberately skip
 * restapi.js's error_intercept (session.js's /user_session client, user.js's
 * api_no_intercept, region.js's no_intercept).
 *
 * Reporting-only by design: it never toasts, navigates or clears session state,
 * because those instances exist precisely to keep error_intercept's side
 * effects away from the login flow — a 401 from WitnessAuthDialog's
 * verify-only /login must not bounce the visitor out of the page they are on.
 *
 * The stamp is applied to every failure, including the ones we stay quiet
 * about, for the same reason error_intercept stamps its own: suppressing our
 * enriched report accomplishes nothing if RUM's context-free duplicate still
 * mints an issue.
 */
export function reportUninterceptedApiError(error) {
  markApiErrorReported(error);
  if (isUnexpectedUninterceptedFailure(error)) {
    reportApiErrorToDatadog(error);
  }
  return Promise.reject(error);
}

/** Wire reportUninterceptedApiError onto an axios instance. Returns it. */
export function attachUninterceptedErrorReporting(instance) {
  instance.interceptors.response.use(undefined, reportUninterceptedApiError);
  return instance;
}

export function markApiErrorReported(error) {
  if (!error || typeof error !== 'object') return error;
  try {
    Object.defineProperty(error, REPORTED, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  } catch {
    // Frozen or sealed error object — the message-shape check below still
    // catches the duplicate, so this is a soft failure by design.
  }
  return error;
}

export function isApiErrorReported(value) {
  return Boolean(value && typeof value === 'object' && value[REPORTED] === true);
}

/**
 * Stamp an error whose user-visible handling is already underway. See HANDLED.
 *
 * Called by restapi.js's handle401 and by region.js's no-organization guard —
 * see HANDLED above for what qualifies. Kept here rather than in restapi.js so
 * both stamps share one implementation and one set of semantics, and so the
 * predicate can be imported by views without dragging in the store layer.
 */
export function markApiErrorHandled(error) {
  if (!error || typeof error !== 'object') return error;
  try {
    Object.defineProperty(error, HANDLED, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  } catch {
    // Frozen or sealed error object. Soft failure by design: an unstamped error
    // reads as "not handled", so the caller logs and notifies. That is the safe
    // direction to fail — a duplicate toast beats a silent dashboard.
  }
  return error;
}

/**
 * Has the app's standard 401 expiry sequence already run (or is it running) for
 * this error?
 *
 * True means the user has been shown a toast and is being redirected to /login,
 * so a view-level catch should stay quiet. False means nothing has explained
 * this failure yet — log, notify and report it, whatever its status code.
 */
export function isApiErrorHandled(value) {
  return Boolean(value && typeof value === 'object' && value[HANDLED] === true);
}

/**
 * beforeSend predicate: true means "drop this RUM event".
 *
 * @param {object} event   the RUM event
 * @param {object} context RUM's domain context; for error events this carries
 *                         `error`, the value that was actually thrown/logged.
 */
export function shouldDiscardRumErrorEvent(event, context) {
  if (event?.type !== 'error') return false;

  // restapi.js's own enriched report — the one issue we DO want to keep.
  // Checked first and returned immediately: its `cause` is the marked original
  // axios error, so any further inspection would discard the very event this
  // whole mechanism exists to surface.
  if (event.context?.chartflow_reported) return false;

  // RUM's own copy of a failure the interceptor already triaged, arriving
  // either as the re-thrown rejection or through a caller's console.error().
  // Covers the failures we deliberately do NOT report (expected login_options
  // 404s, caller-aborted requests) as well as the ones we do — suppressing our
  // enriched report is pointless if RUM's context-free copy still mints an issue.
  if (isApiErrorReported(context?.error)) return true;

  // Fallback for captures that reach RUM without the original error attached —
  // RUM's console channel only carries it when the caller's console.error()
  // arguments actually include the Error object.
  //
  // Every axios instance in the app now stamps its failures: error_intercept
  // for the regular clients, reportUninterceptedApiError for the auth-surface
  // ones (session.js, user.js's api_no_intercept, region.js's no_intercept).
  // So a generic axios message arriving unstamped means the stamp was lost in
  // transit, not that the failure went unreported — the enriched replacement
  // exists either way, and dropping this copy is what keeps it to one issue.
  return GENERIC_AXIOS_FAILURE.test(event.error?.message || '');
}
